import React, { useState } from 'react';
import { X, Zap, Award, ArrowRight, Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { TRANSITIONS } from '../../utils/animations';
import { usePromotions } from '../../hooks/usePromotions';

interface UpgradePromptProps {
  onClose: () => void;
}

export default function UpgradePrompt({ onClose }: UpgradePromptProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'pro'>('premium');
  const navigate = useNavigate();
  const isPro = useUserStore(state => state.isPro);
  const isPremium = useUserStore(state => state.isPremium);
  const isAdmin = useUserStore(state => state.isAdmin);
  const { formattedTimeRemaining } = usePromotions();

  // Check if user has access to Pro (either Pro user or Admin)
  const hasProAccess = isPro() || isAdmin();
  const hasPremiumAccess = isPremium() || hasProAccess;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleUpgrade = () => {
    navigate('/dashboard/precos');
    onClose();
  };

  // If user is admin or already has pro, don't show the popup
  if (isAdmin() || (hasProAccess && hasPremiumAccess)) {
    return null;
  }

  // For Premium users, default to Pro plan
  // For Free users, default to Premium plan
  const defaultPlan = hasPremiumAccess ? 'pro' : 'premium';
  if (selectedPlan !== defaultPlan) {
    setSelectedPlan(defaultPlan);
  }

  const premiumFeatures = [
    'Ideias ilimitadas de conteúdo',
    'Análises avançadas de engajamento',
    'Planejamento estratégico',
    'Suporte prioritário'
  ];

  const proFeatures = [
    'Tudo do Premium',
    'Consultoria mensal',
    'Acesso antecipado a recursos',
    'Suporte VIP por chat ao vivo'
  ];

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
      <div className={`bg-white rounded-xl max-w-md w-full mx-4 overflow-hidden shadow-2xl ${TRANSITIONS.DEFAULT} ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-x-10 -translate-y-20 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full translate-x-5 translate-y-20 blur-2xl"></div>
          
          <div className="relative">
            <h2 className="text-2xl font-bold mb-2">Desbloqueie Todo o Potencial</h2>
            <p className="text-white/90">
              Aumente seu alcance e engajamento com recursos premium
            </p>
            <div className="mt-2 flex items-center text-white/90 text-sm">
              <Clock className="h-4 w-4 mr-1" />
              <span>Oferta expira em: {formattedTimeRemaining}</span>
            </div>
          </div>
        </div>

        {/* Plan selection */}
        <div className="p-6">
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setSelectedPlan('premium')}
              className={`flex-1 py-3 px-4 rounded-lg transition-all ${
                selectedPlan === 'premium'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              disabled={hasPremiumAccess}
            >
              <div className="flex items-center justify-center">
                <Zap className={`h-5 w-5 ${selectedPlan === 'premium' ? 'text-yellow-300' : 'text-indigo-600'} mr-2`} />
                <span>Premium</span>
              </div>
            </button>
            <button
              onClick={() => setSelectedPlan('pro')}
              className={`flex-1 py-3 px-4 rounded-lg transition-all ${
                selectedPlan === 'pro'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center justify-center">
                <Award className={`h-5 w-5 ${selectedPlan === 'pro' ? 'text-yellow-300' : 'text-indigo-600'} mr-2`} />
                <span>Pro</span>
              </div>
            </button>
          </div>

          {/* Features list */}
          <div className="space-y-3 mb-6">
            {(selectedPlan === 'premium' ? premiumFeatures : proFeatures).map((feature, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 mr-3">
                  <Check className="h-3 w-3 text-green-600" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                R${selectedPlan === 'premium' ? '49' : '99'}
              </span>
              <span className="ml-1 text-gray-600">/mês</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Economize 20% com o plano anual
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleUpgrade}
            className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center group"
          >
            Fazer Upgrade Agora
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}